import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Logo } from "./Logo";
import { bandColor } from "../lib/perf";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "../i18n";
import { DownloadSimple, Copy, ShareNetwork, CheckCircle, WhatsappLogo, XLogo, InstagramLogo } from "@phosphor-icons/react";

export const ShareCard = ({ open, onOpenChange, title, bigValue, unit, subtitle, band = "good", context, badge, logos, shareText, onShared, avatarName, avatarColor, localize }) => {
  const { t, i18n } = useTranslation();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const blobUrls = useRef(new Set());
  useEffect(() => () => { blobUrls.current.forEach((u) => URL.revokeObjectURL(u)); blobUrls.current.clear(); }, []);
  const [cardLang, setCardLang] = useState(i18n.language?.slice(0, 2) || "fr");
  // Si `localize` est fourni, la carte peut être rendue dans une langue choisie
  // par celui qui partage (utile pour partager avec un ami étranger).
  const loc = localize ? localize(i18n.getFixedT(cardLang)) : null;
  title = loc?.title ?? title;
  bigValue = loc?.bigValue ?? bigValue;
  unit = loc?.unit ?? unit;
  subtitle = loc?.subtitle ?? subtitle;
  context = loc?.context ?? context;
  badge = loc?.badge ?? badge;
  shareText = loc?.shareText ?? shareText;
  const color = bandColor(band);
  const caption = shareText || title || "HITMAX";

  const render = async () => {
    if (!cardRef.current) return null;
    return await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
  };

  const renderBlob = async () => {
    const url = await render();
    return await (await fetch(url)).blob();
  };

  const copyBlob = async () => {
    try {
      const blob = await renderBlob();
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      return true;
    } catch { return false; }
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = await render();
      const a = document.createElement("a");
      a.href = url;
      a.download = "hitmax-card.png";
      a.click();
      toast.success(t("share.downloaded"));
      onShared && onShared("download");
    } catch { toast.error(t("share.download_fail")); }
    setBusy(false);
  };

  const copy = async () => {
    setBusy(true);
    try {
      const url = await render();
      const blob = await (await fetch(url)).blob();
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      toast.success(t("share.copied"));
      onShared && onShared("copy");
    } catch { toast.error(t("share.copy_unsupported")); }
    setBusy(false);
  };

  const nativeShare = async () => {
    setBusy(true);
    try {
      const blob = await renderBlob();
      const file = new File([blob], "hitmax-card.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption, title: "HITMAX" });
        onShared && onShared("native");
      } else {
        toast.info(t("share.native_unavailable"));
      }
    } catch {}
    setBusy(false);
  };

  // Partage en 1 clic. L'image est copiée dans le presse-papiers pour être
  // collée dans WhatsApp/X, et on ouvre le réseau avec la légende pré-remplie.
  const shareWhatsApp = async () => {
    setBusy(true);
    const file = await (async () => { try { const b = await renderBlob(); return new File([b], "hitmax-card.png", { type: "image/png" }); } catch { return null; } })();
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], text: caption }); onShared && onShared("whatsapp"); setBusy(false); return; } catch {}
    }
    const copied = await copyBlob();
    if (copied) toast.success(t("share.copied_whatsapp"));
    window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank", "noopener");
    onShared && onShared("whatsapp");
    setBusy(false);
  };

  const shareX = async () => {
    setBusy(true);
    const copied = await copyBlob();
    if (copied) toast.success(t("share.copied_x"));
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`, "_blank", "noopener");
    onShared && onShared("x");
    setBusy(false);
  };

  const shareInstagram = async () => {
    setBusy(true);
    try {
      const blob = await renderBlob();
      const file = new File([blob], "hitmax-card.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption });
      } else {
        const url = URL.createObjectURL(blob);
        blobUrls.current.add(url);
        const a = document.createElement("a");
        a.href = url; a.download = "hitmax-card.png"; a.click();
        setTimeout(() => { URL.revokeObjectURL(url); blobUrls.current.delete(url); }, 1000); // téléchargement démarré
        toast.info(t("share.saved_instagram"));
      }
      onShared && onShared("instagram");
    } catch {}
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface-base border-white/10 max-w-sm p-5">
        <DialogTitle className="sr-only">{t("share.dialog_title")}</DialogTitle>
        {localize && (
          <div className="flex items-center justify-center gap-1.5 mb-3" data-testid="share-lang-toggle">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                type="button"
                data-testid={`share-lang-${l.code}`}
                onClick={() => setCardLang(l.code)}
                className={`px-2.5 py-1 rounded-full text-sm transition-colors ${cardLang === l.code ? "bg-gold/20 border border-gold/50" : "bg-white/5 border border-transparent opacity-60 hover:opacity-100"}`}
                aria-label={l.label}
              >
                {l.flag}
              </button>
            ))}
          </div>
        )}
        <div
          ref={cardRef}
          data-testid="share-card-preview"
          style={{
            background: "linear-gradient(160deg, #0A0A0A 0%, #141210 60%, #0A0A0A 100%)",
            aspectRatio: "9/16",
          }}
          className="relative rounded-2xl p-6 flex flex-col overflow-hidden border border-white/10"
        >
          <div
            className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-25"
            style={{ background: color }}
          />
          <div className="flex items-center justify-between relative">
            <Logo size="h-10" />
            <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-white/40">Insight</span>
          </div>
          {badge && (
            <div className="relative flex justify-center mt-4" data-testid="share-confirmed-badge">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em]"
                style={{ background: "rgba(0,230,118,0.16)", border: "1px solid rgba(0,230,118,0.55)", color: "#00E676" }}
              >
                <CheckCircle size={15} weight="fill" color="#00E676" />
                {badge}
              </span>
            </div>
          )}
          <div className="flex-1 flex flex-col items-center justify-center text-center relative">
            {avatarName && (
              <div className="flex flex-col items-center mb-4" data-testid="share-avatar">
                <div className="w-16 h-16 rounded-full flex items-center justify-center font-display font-black text-2xl"
                  style={{ background: `${avatarColor || color}22`, border: `2px solid ${avatarColor || color}`, color: avatarColor || color }}>
                  {avatarName.trim().slice(0, 2).toUpperCase()}
                </div>
                <span className="mt-2 text-base font-black text-white/90 px-2 text-center">{avatarName}</span>
              </div>
            )}
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/50 mb-4">{title}</span>
            {logos && logos.length === 2 && (
              <div className="flex items-center justify-center gap-5 mb-4" data-testid="share-logos">
                {[logos[0], logos[1]].map((lg, i) => (
                  <React.Fragment key={i}>
                    {lg?.crest ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/img/proxy?url=${encodeURIComponent(lg.crest)}`}
                        alt={lg.name || ""} crossOrigin="anonymous"
                        style={{ width: 56, height: 56, objectFit: "contain" }} />
                    ) : (
                      <span className="font-bold text-white/80 text-lg">{lg?.short || lg?.name}</span>
                    )}
                    {i === 0 && <span className="text-white/40 text-sm font-bold tracking-widest">VS</span>}
                  </React.Fragment>
                ))}
              </div>
            )}
            <span className="font-numeral font-black leading-none" style={{ color, fontSize: 96 }}>
              {bigValue}
              {unit && <span style={{ fontSize: 34 }}>{unit}</span>}
            </span>
            {subtitle && <span className="mt-4 text-sm font-semibold text-white/70 max-w-[80%]">{subtitle}</span>}
          </div>
          {context && (
            <div className="relative text-center text-[11px] font-medium text-white/40">{context}</div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4" data-testid="share-quick-row">
          <Button onClick={shareWhatsApp} disabled={busy} data-testid="share-whatsapp"
            className="text-white font-bold" style={{ background: "#25D366" }}>
            <WhatsappLogo size={18} weight="fill" /> <span className="ml-1.5 text-xs">WhatsApp</span>
          </Button>
          <Button onClick={shareX} disabled={busy} data-testid="share-x"
            className="text-white font-bold bg-black hover:bg-black/80 border border-white/20">
            <XLogo size={17} weight="fill" /> <span className="ml-1.5 text-xs">X</span>
          </Button>
          <Button onClick={shareInstagram} disabled={busy} data-testid="share-instagram"
            className="text-white font-bold" style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }}>
            <InstagramLogo size={18} weight="fill" /> <span className="ml-1.5 text-xs">Insta</span>
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-2">
          <Button onClick={download} disabled={busy} data-testid="share-download" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/5">
            <DownloadSimple size={18} weight="bold" />
          </Button>
          <Button onClick={copy} disabled={busy} data-testid="share-copy" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/5">
            <Copy size={18} weight="bold" />
          </Button>
          <Button onClick={nativeShare} disabled={busy} data-testid="share-native" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/5">
            <ShareNetwork size={18} weight="bold" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
